import { useState } from 'react';
import {
  alpha,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Braces,
  Check,
  ChevronDown,
  ChevronRight,
  Inbox,
  PackageOpen,
  Sparkles,
} from 'lucide-react';

import { Stack } from '$layouts/v2/components/Stack.jsx';
import { KIND_META, KindIcon } from '$layouts/v2/consts';
import { type FieldInfo, fieldTypeLabel } from '$layouts/v2/helpers/context.js';

export interface ContextFieldGroup {
  source: {
    id: string;
    label: string;
    kind?: FieldInfo['producedByKind'];
    category: 'email' | 'workflow-inputs' | 'this-step' | 'step';
  };
  fields: FieldInfo[];
}

/** A short type label that stays legible inside a compact data chip. */
export function contextFieldTypeLabel(field: FieldInfo): string {
  switch (field.type) {
    case 'array': {
      return 'List';
    }
    case 'object': {
      return 'Object';
    }
    case 'select': {
      return field.options?.length
        ? `${field.options.length} choices`
        : 'Choice';
    }
    case 'checkbox': {
      return 'Yes / no';
    }
    case 'email': {
      return 'Email';
    }
    default: {
      return field.type;
    }
  }
}

/** A friendly source label for data that is available in a workflow step. */
export function contextSourceLabel(field: FieldInfo): string {
  if (field.producedByLabel?.trim()) {
    return field.producedByLabel;
  }
  if (field.producedBy === 'email') {
    return 'Incoming email';
  }
  if (field.producedBy === '(workflow inputs)') {
    return 'Workflow inputs';
  }
  if (field.producedBy === 'this step') {
    return 'This step’s output';
  }
  return 'A previous step';
}

/** Group context fields in the order people encounter them in the editor. */
export function groupContextFields(fields: FieldInfo[]): ContextFieldGroup[] {
  const groups = new Map<string, ContextFieldGroup>();

  for (const field of fields) {
    const source = sourceFor(field);
    const existing = groups.get(source.id);
    if (existing) {
      existing.fields.push(field);
    } else {
      groups.set(source.id, { source, fields: [field] });
    }
  }

  return [...groups.values()]
    .map((group) => ({
      ...group,
      fields: [...group.fields].toSorted((a, b) =>
        a.name.localeCompare(b.name),
      ),
    }))
    .toSorted((a, b) => {
      const rank =
        sourceRank(a.source.category) - sourceRank(b.source.category);
      return rank || a.source.label.localeCompare(b.source.label);
    });
}

/**
 * A single visual language for context throughout the workflow editor.
 * Fields stay grouped by their source, while selection/used state can be
 * supplied by the surrounding control.
 */
export function ContextFieldGroups({
  fields,
  selectedNames,
  selectedLabel = 'selected',
  selectionColor = 'primary',
  onFieldClick,
  emptyLabel = 'No data is available here yet.',
  compact = false,
  maxHeight,
}: {
  fields: FieldInfo[];
  selectedNames?: ReadonlySet<string>;
  selectedLabel?: string;
  selectionColor?: 'primary' | 'success';
  onFieldClick?: (field: FieldInfo) => void;
  emptyLabel?: string;
  compact?: boolean;
  maxHeight?: number | string;
}) {
  const groups = groupContextFields(fields);

  if (groups.length === 0) {
    return (
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <Stack
      spacing={compact ? 0.75 : 1}
      sx={{
        maxHeight,
        overflowY: maxHeight ? 'auto' : undefined,
        pr: maxHeight ? 0.5 : undefined,
      }}
    >
      {groups.map((group) => {
        const selectedCount = group.fields.filter((field) =>
          selectedNames?.has(field.name),
        ).length;
        const accent = sourceAccent(group.source);

        return (
          <Box
            key={group.source.id}
            sx={(theme) => ({
              border: 1,
              borderColor: 'divider',
              borderRadius: 1.25,
              p: compact ? 0.75 : 1,
              bgcolor: alpha(
                accent,
                theme.palette.mode === 'dark' ? 0.08 : 0.045,
              ),
            })}
          >
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ minWidth: 0 }}
            >
              <ContextSourceMark source={group.source} accent={accent} />
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, minWidth: 0, flexGrow: 1 }}
                noWrap
              >
                {group.source.label}
              </Typography>
              <Typography
                variant="caption"
                noWrap
                sx={{
                  color: 'text.secondary',
                }}
              >
                {group.fields.length}{' '}
                {group.fields.length === 1 ? 'field' : 'fields'}
              </Typography>
              {selectedCount > 0 && (
                <Chip
                  size="small"
                  color={selectionColor}
                  label={`${selectedCount} ${selectedLabel}`}
                  sx={{ height: 20, '& .MuiChip-label': { px: 0.75 } }}
                />
              )}
            </Stack>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                mt: compact ? 0.625 : 0.75,
              }}
            >
              {group.fields.map((field) => {
                const selected = selectedNames?.has(field.name) ?? false;
                return (
                  <ContextFieldChip
                    key={field.name}
                    field={field}
                    sourceLabel={group.source.label}
                    selected={selected}
                    selectionColor={selectionColor}
                    onClick={onFieldClick}
                  />
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

type TemplatePathUsage = 'none' | 'partial' | 'exact';

/**
 * The schema-aware variant used by the template editor. It deliberately keeps
 * object/list navigation here (rather than making every context picker more
 * complex), because only a template can address values below a top-level
 * field.
 */
export function TemplateContextFieldGroups({
  fields,
  usedPaths,
  onPathClick,
  emptyLabel = 'No data is available here yet.',
  maxHeight,
}: {
  fields: FieldInfo[];
  /** Canonical paths referenced by the template, such as `order.customer.name`. */
  usedPaths?: ReadonlySet<string>;
  /** Called with a bracket-syntax path ready to insert into a template. */
  onPathClick?: (path: string, field: FieldInfo) => void;
  emptyLabel?: string;
  maxHeight?: number | string;
}) {
  const groups = groupContextFields(fields);
  const [expandedPaths, setExpandedPaths] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [arrayIndexes, setArrayIndexes] = useState<Record<string, string>>({});

  const toggleExpanded = (path: string) => {
    setExpandedPaths((current) => {
      const next = new Set(current);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const setArrayIndex = (path: string, value: string) => {
    if (!/^\d*$/.test(value)) {
      return;
    }
    setArrayIndexes((current) => ({ ...current, [path]: value }));
  };

  if (groups.length === 0) {
    return (
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
        }}
      >
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <Stack
      spacing={0.75}
      sx={{
        maxHeight,
        overflowY: maxHeight ? 'auto' : undefined,
        pr: maxHeight ? 0.5 : undefined,
      }}
    >
      {groups.map((group) => {
        const accent = sourceAccent(group.source);
        const usedCount = new Set(
          group.fields.flatMap((field) =>
            [...(usedPaths ?? [])].filter((path) =>
              pathBelongsToField(path, field.name),
            ),
          ),
        ).size;

        return (
          <Box
            key={group.source.id}
            sx={(theme) => ({
              border: 1,
              borderColor: 'divider',
              borderRadius: 1.25,
              p: 0.75,
              bgcolor: alpha(
                accent,
                theme.palette.mode === 'dark' ? 0.08 : 0.045,
              ),
            })}
          >
            <Stack
              direction="row"
              spacing={0.75}
              alignItems="center"
              sx={{ minWidth: 0 }}
            >
              <ContextSourceMark source={group.source} accent={accent} />
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, minWidth: 0, flexGrow: 1 }}
                noWrap
              >
                {group.source.label}
              </Typography>
              <Typography
                variant="caption"
                noWrap
                sx={{
                  color: 'text.secondary',
                }}
              >
                {group.fields.length}{' '}
                {group.fields.length === 1 ? 'field' : 'fields'}
              </Typography>
              {usedCount > 0 && (
                <Chip
                  size="small"
                  color="success"
                  label={`${usedCount} used`}
                  sx={{ height: 20, '& .MuiChip-label': { px: 0.75 } }}
                />
              )}
            </Stack>

            <Stack spacing={0.5} sx={{ mt: 0.625 }}>
              {group.fields.map((field) => (
                <TemplateSchemaField
                  key={field.name}
                  field={field}
                  path={field.name}
                  displayName={field.name}
                  usedPaths={usedPaths}
                  onPathClick={onPathClick}
                  isExpanded={(path) => expandedPaths.has(path)}
                  onToggle={toggleExpanded}
                  arrayIndexForPath={(path) => arrayIndexes[path] ?? '0'}
                  onArrayIndexChange={setArrayIndex}
                />
              ))}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}

function TemplateSchemaField({
  field,
  path,
  displayName,
  usedPaths,
  onPathClick,
  isExpanded,
  onToggle,
  arrayIndexForPath,
  onArrayIndexChange,
  depth = 0,
}: {
  field: FieldInfo;
  /** A canonical reference path; arrays always use `[index]`. */
  path: string;
  displayName: string;
  usedPaths?: ReadonlySet<string>;
  onPathClick?: (path: string, field: FieldInfo) => void;
  isExpanded: (path: string) => boolean;
  onToggle: (path: string) => void;
  arrayIndexForPath: (path: string) => string;
  onArrayIndexChange: (path: string, value: string) => void;
  depth?: number;
}) {
  const structured = field.type === 'object' || field.type === 'array';
  const usage = templatePathUsage(path, usedPaths);
  const expanded = isExpanded(path);
  const currentIndex = arrayIndexForPath(path) || '0';
  const itemPath = `${path}[${currentIndex}]`;

  return (
    <Box>
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          minWidth: 0,
          border: 1,
          borderColor:
            usage === 'exact'
              ? theme.palette.success.main
              : usage === 'partial'
              ? alpha(theme.palette.success.main, 0.5)
              : 'divider',
          borderRadius: 1,
          px: 0.5,
          py: 0.4,
          bgcolor:
            usage === 'exact'
              ? alpha(
                  theme.palette.success.main,
                  theme.palette.mode === 'dark' ? 0.18 : 0.08,
                )
              : undefined,
        })}
      >
        {structured ? (
          <IconButton
            size="small"
            aria-label={`${
              expanded ? 'Hide' : 'Show'
            } fields inside ${displayName}`}
            onClick={() => onToggle(path)}
            sx={{ flex: '0 0 auto', ml: depth ? -0.25 : 0 }}
          >
            {expanded ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
          </IconButton>
        ) : (
          <Box sx={{ width: 28, flex: '0 0 auto' }} />
        )}

        <Tooltip
          arrow
          title={
            structured
              ? `Insert {{${path}}} as ${
                  field.type === 'array' ? 'JSON list data' : 'JSON object data'
                }.`
              : `Insert {{${path}}}.`
          }
        >
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography
              variant="caption"
              sx={{ display: 'block', fontWeight: 700 }}
              noWrap
            >
              {displayName}
            </Typography>
            <Typography
              variant="caption"
              noWrap
              sx={{
                color: 'text.secondary',
                display: 'block',
                fontSize: '0.68rem',
              }}
            >
              {`{{${path}}}`} · {contextFieldTypeLabel(field)}
            </Typography>
          </Box>
        </Tooltip>

        {usage !== 'none' && (
          <Chip
            size="small"
            color="success"
            variant={usage === 'exact' ? 'filled' : 'outlined'}
            icon={usage === 'exact' ? <Check size={12} /> : undefined}
            label={usage === 'exact' ? 'Used' : 'Partly used'}
            sx={{
              height: 22,
              flex: '0 0 auto',
              '& .MuiChip-label': { px: 0.625 },
            }}
          />
        )}

        {onPathClick && (
          <Button
            size="small"
            onClick={() => onPathClick(path, field)}
            sx={{ minWidth: structured ? 68 : 54, px: 0.75, flex: '0 0 auto' }}
          >
            {field.type === 'array'
              ? 'Use list'
              : field.type === 'object'
              ? 'Use object'
              : 'Insert'}
          </Button>
        )}
      </Box>

      {structured && (
        <Collapse in={expanded} unmountOnExit>
          <Box
            sx={{
              borderLeft: 1,
              borderColor: 'divider',
              ml: 1.75,
              mt: 0.5,
              pl: 0.75,
            }}
          >
            {field.type === 'object' && (
              <SchemaObjectFields
                fields={field.properties}
                parentPath={path}
                usedPaths={usedPaths}
                onPathClick={onPathClick}
                isExpanded={isExpanded}
                onToggle={onToggle}
                arrayIndexForPath={arrayIndexForPath}
                onArrayIndexChange={onArrayIndexChange}
                depth={depth + 1}
              />
            )}
            {field.type === 'array' && (
              <SchemaArrayItems
                field={field}
                path={path}
                itemPath={itemPath}
                itemIndex={currentIndex}
                usedPaths={usedPaths}
                onPathClick={onPathClick}
                isExpanded={isExpanded}
                onToggle={onToggle}
                arrayIndexForPath={arrayIndexForPath}
                onArrayIndexChange={onArrayIndexChange}
                depth={depth + 1}
              />
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}

function SchemaObjectFields({
  fields,
  parentPath,
  usedPaths,
  onPathClick,
  isExpanded,
  onToggle,
  arrayIndexForPath,
  onArrayIndexChange,
  depth,
}: {
  fields?: FieldInfo[];
  parentPath: string;
  usedPaths?: ReadonlySet<string>;
  onPathClick?: (path: string, field: FieldInfo) => void;
  isExpanded: (path: string) => boolean;
  onToggle: (path: string) => void;
  arrayIndexForPath: (path: string) => string;
  onArrayIndexChange: (path: string, value: string) => void;
  depth: number;
}) {
  if (!fields?.length) {
    return (
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          py: 0.5,
          display: 'block',
        }}
      >
        This object has no field schema yet.
      </Typography>
    );
  }

  return (
    <Stack spacing={0.5}>
      {fields.map((property) => {
        const propertyPath = `${parentPath}.${property.name}`;
        return (
          <TemplateSchemaField
            key={propertyPath}
            field={property}
            path={propertyPath}
            displayName={property.name}
            usedPaths={usedPaths}
            onPathClick={onPathClick}
            isExpanded={isExpanded}
            onToggle={onToggle}
            arrayIndexForPath={arrayIndexForPath}
            onArrayIndexChange={onArrayIndexChange}
            depth={depth}
          />
        );
      })}
    </Stack>
  );
}

function SchemaArrayItems({
  field,
  path,
  itemPath,
  itemIndex,
  usedPaths,
  onPathClick,
  isExpanded,
  onToggle,
  arrayIndexForPath,
  onArrayIndexChange,
  depth,
}: {
  field: FieldInfo;
  path: string;
  itemPath: string;
  itemIndex: string;
  usedPaths?: ReadonlySet<string>;
  onPathClick?: (path: string, field: FieldInfo) => void;
  isExpanded: (path: string) => boolean;
  onToggle: (path: string) => void;
  arrayIndexForPath: (path: string) => string;
  onArrayIndexChange: (path: string, value: string) => void;
  depth: number;
}) {
  const item = field.items;
  const itemUsage = templatePathUsage(itemPath, usedPaths);

  if (!item) {
    return (
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          py: 0.5,
          display: 'block',
        }}
      >
        The items in this list do not have a schema yet.
      </Typography>
    );
  }

  return (
    <Stack spacing={0.5}>
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{ py: 0.125 }}
      >
        <Typography
          variant="caption"
          noWrap
          sx={{
            color: 'text.secondary',
          }}
        >
          Item #
        </Typography>
        <TextField
          size="small"
          value={itemIndex}
          type="text"
          inputMode="numeric"
          onChange={
            onPathClick
              ? (event) => onArrayIndexChange(path, event.target.value)
              : undefined
          }
          slotProps={{
            htmlInput: {
              'aria-label': `Item index for ${path}`,
              inputMode: 'numeric',
              pattern: '[0-9]*',
              readOnly: !onPathClick,
            },
          }}
          sx={{
            width: 54,
            '& .MuiInputBase-input': { py: 0.4, px: 0.75, textAlign: 'center' },
          }}
        />
        <Typography
          variant="caption"
          noWrap
          sx={{
            color: 'text.secondary',
          }}
        >
          0 is the first item
        </Typography>
      </Stack>

      {item.type === 'object' && (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              flexGrow: 1,
            }}
          >
            Use the whole selected item, or choose one of its fields.
          </Typography>
          {itemUsage !== 'none' && (
            <Chip
              size="small"
              color="success"
              variant={itemUsage === 'exact' ? 'filled' : 'outlined'}
              label={itemUsage === 'exact' ? 'Used' : 'Partly used'}
              sx={{ height: 22, '& .MuiChip-label': { px: 0.625 } }}
            />
          )}
          {onPathClick && (
            <Button
              size="small"
              onClick={() => onPathClick(itemPath, item)}
              sx={{ minWidth: 62, px: 0.75, flex: '0 0 auto' }}
            >
              Use item
            </Button>
          )}
        </Stack>
      )}

      {item.type === 'object' ? (
        <SchemaObjectFields
          fields={item.properties}
          parentPath={itemPath}
          usedPaths={usedPaths}
          onPathClick={onPathClick}
          isExpanded={isExpanded}
          onToggle={onToggle}
          arrayIndexForPath={arrayIndexForPath}
          onArrayIndexChange={onArrayIndexChange}
          depth={depth}
        />
      ) : (
        <TemplateSchemaField
          field={item}
          path={itemPath}
          displayName="Item value"
          usedPaths={usedPaths}
          onPathClick={onPathClick}
          isExpanded={isExpanded}
          onToggle={onToggle}
          arrayIndexForPath={arrayIndexForPath}
          onArrayIndexChange={onArrayIndexChange}
          depth={depth}
        />
      )}
    </Stack>
  );
}

/** The exact selected path, a selected descendant, or no selection. */
export function templatePathUsage(
  path: string,
  usedPaths?: ReadonlySet<string>,
): TemplatePathUsage {
  if (!usedPaths?.size) {
    return 'none';
  }
  if (usedPaths.has(path)) {
    return 'exact';
  }
  const childPrefix = `${path}.`;
  const arrayPrefix = `${path}[`;
  return [...usedPaths].some(
    (usedPath) =>
      usedPath.startsWith(childPrefix) || usedPath.startsWith(arrayPrefix),
  )
    ? 'partial'
    : 'none';
}

function pathBelongsToField(path: string, fieldName: string) {
  return (
    path === fieldName ||
    path.startsWith(`${fieldName}.`) ||
    path.startsWith(`${fieldName}[`)
  );
}

function ContextFieldChip({
  field,
  sourceLabel,
  selected,
  selectionColor,
  onClick,
}: {
  field: FieldInfo;
  sourceLabel: string;
  selected: boolean;
  selectionColor: 'primary' | 'success';
  onClick?: (field: FieldInfo) => void;
}) {
  return (
    <Tooltip
      arrow
      title={
        <Box sx={{ p: 0.25 }}>
          <Typography
            variant="caption"
            sx={{ display: 'block', fontWeight: 700 }}
          >
            {field.name}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            {fieldTypeLabel(field)}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
            Provided by {sourceLabel}
          </Typography>
        </Box>
      }
    >
      <Chip
        size="small"
        clickable={Boolean(onClick)}
        onClick={onClick ? () => onClick(field) : undefined}
        color={selected ? selectionColor : 'default'}
        variant={selected ? 'filled' : 'outlined'}
        label={
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              maxWidth: 220,
            }}
          >
            <Box
              component="span"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {field.name}
            </Box>
            <Box
              component="span"
              sx={{
                color: selected ? 'inherit' : 'text.secondary',
                fontSize: '0.7rem',
                whiteSpace: 'nowrap',
              }}
            >
              {contextFieldTypeLabel(field)}
            </Box>
          </Box>
        }
        sx={{
          height: 28,
          maxWidth: '100%',
          '& .MuiChip-label': { minWidth: 0 },
        }}
      />
    </Tooltip>
  );
}

function sourceFor(field: FieldInfo): ContextFieldGroup['source'] {
  if (field.producedBy === 'email') {
    return {
      id: 'email',
      label: contextSourceLabel(field),
      category: 'email',
    };
  }
  if (field.producedBy === '(workflow inputs)') {
    return {
      id: 'workflow-inputs',
      label: contextSourceLabel(field),
      category: 'workflow-inputs',
    };
  }
  if (field.producedBy === 'this step') {
    return {
      id: 'this-step',
      label: contextSourceLabel(field),
      kind: field.producedByKind,
      category: 'this-step',
    };
  }
  return {
    id: field.producedBy,
    label: contextSourceLabel(field),
    kind: field.producedByKind,
    category: 'step',
  };
}

function sourceRank(category: ContextFieldGroup['source']['category']) {
  switch (category) {
    case 'email': {
      return 0;
    }
    case 'workflow-inputs': {
      return 1;
    }
    case 'step': {
      return 2;
    }
    case 'this-step': {
      return 3;
    }
  }
}

function sourceAccent(source: ContextFieldGroup['source']) {
  if (source.kind) {
    return KIND_META[source.kind].color;
  }
  switch (source.category) {
    case 'email': {
      return '#0ea5e9';
    }
    case 'workflow-inputs': {
      return '#6366f1';
    }
    case 'this-step': {
      return '#22c55e';
    }
    default: {
      return '#64748b';
    }
  }
}

function ContextSourceMark({
  source,
  accent,
}: {
  source: ContextFieldGroup['source'];
  accent: string;
}) {
  if (source.kind) {
    return (
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: 0.75,
          display: 'grid',
          placeItems: 'center',
          flex: '0 0 auto',
          color: accent,
          bgcolor: alpha(accent, 0.14),
        }}
      >
        <KindIcon kind={source.kind} size={13} />
      </Box>
    );
  }
  let Icon = PackageOpen;
  switch (source.category) {
    case 'email': {
      Icon = Inbox;
      break;
    }
    case 'workflow-inputs': {
      Icon = Braces;
      break;
    }
    case 'this-step': {
      Icon = Sparkles;
      break;
    }
    default: {
      break;
    }
  }

  return (
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: 0.75,
        display: 'grid',
        placeItems: 'center',
        flex: '0 0 auto',
        color: accent,
        bgcolor: alpha(accent, 0.14),
      }}
    >
      <Icon size={13} />
    </Box>
  );
}
